import React, { useState, useEffect } from "react";
import ImageUpload from "./ImageUpload";
import SocialMediaFormats from "./SocialMediaFormats";
import { baseUrl } from "../config";
import { trackEvent } from "../components/trackingUtils";
import SEOHead from "../common/SEOHead";
import Reveal from "../components/Reveal";
import "./ImageConverter.css";

const ImageConverter = () => {
  const [socialMediaPaths, setSocialMediaPaths] = useState({
    twitter: null,
    facebook: null,
    whatsapp: null,
    linkedin: null,
  });
  const [uploadError, setUploadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingStep, setProcessingStep] = useState(1);
  const [conversionProgress, setConversionProgress] = useState(0);

  useEffect(() => {
    trackEvent("PageLoad", "", "ImageConverter");
  }, []);

  const handleImageUpload = (imageUrl, paths, error) => {
    trackEvent(
      "ImageConverter",
      "Upload_Complete",
      paths ? "Success" : "Error"
    );

    if (error) {
      setUploadError(true);
      setProcessingStep(1);
    } else {
      setSocialMediaPaths(paths);
      setUploadError(false);
      setProcessingStep(3);
      setConversionProgress(100);
    }
    setLoading(false);
  };

  const handleUploadStart = () => {
    trackEvent("ImageConverter", "Upload_Start", "User_Action");
    setLoading(true);
    setProcessingStep(2);
    setConversionProgress(25);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setConversionProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 15;
      });
    }, 500);
  };

  const handleReset = () => {
    trackEvent("ImageConverter", "Reset", "User_Action");
    setSocialMediaPaths({
      twitter: null,
      facebook: null,
      whatsapp: null,
      linkedin: null,
    });
    setUploadError(false);
    setLoading(false);
    setProcessingStep(1);
    setConversionProgress(0);
  };

  const title =
    "Free Online Image Converter | Convert Images to Social Media Formats";
  const description =
    "Easily convert your images into various social media formats with our free online image converter tool. Upload your image, customize formats for platforms like Twitter, Facebook, LinkedIn, and WhatsApp, and download your optimized images quickly and effortlessly. Try it now and enhance your social media presence!";
  const keywords =
    "Image Converter,Online Image Converter,Convert Images to Social Media Formats,Image Upload and Conversion Tool,Free Image Format Converter,Social Media Image Resizer,Image Converter Tool Online";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
        image={`${baseUrl}/images/ImageConverter.png`}
        url={`${baseUrl}/tools/imageconverter`}
        type="website"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
          <div
            className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <Reveal>
            <div className="max-w-4xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Free Image{" "}
                </span>
                <span className="bg-gradient-to-r from-[#E5C558] to-[#f5d56d] bg-clip-text text-transparent">
                  Converter
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Transform your images into perfect social media formats
                instantly. Upload once, get optimized versions for all major
                platforms.
              </p>

              {/* Feature Icons */}
              <div className="grid md:grid-cols-4 gap-6 mt-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#E5C558]/20 to-[#d4b54d]/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-8 h-8 text-[#E5C558]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-200">Multi-Format</h3>
                  <p className="text-sm text-gray-400">
                    Twitter, Facebook, LinkedIn, WhatsApp
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-400/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-8 h-8 text-green-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-200">
                    Lightning Fast
                  </h3>
                  <p className="text-sm text-gray-400">Instant conversions</p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-blue-400/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-8 h-8 text-blue-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-200">100% Secure</h3>
                  <p className="text-sm text-gray-400">
                    Your images stay private
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-purple-400/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                    <svg
                      className="w-8 h-8 text-purple-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-200">
                    Completely Free
                  </h3>
                  <p className="text-sm text-gray-400">
                    No limits, no watermarks
                  </p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto py-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Progress Steps */}
          <Reveal>
            <div className="mb-12">
              <div className="flex items-center justify-center space-x-8">
                <div
                  className={`flex items-center ${
                    processingStep >= 1 ? "text-[#E5C558]" : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold ${
                      processingStep >= 1
                        ? "bg-[#E5C558] border-[#E5C558] text-gray-900"
                        : "border-gray-300"
                    }`}
                  >
                    1
                  </div>
                  <span className="ml-3 font-semibold">Upload Image</span>
                </div>

                <div
                  className={`h-1 w-16 rounded-full ${
                    processingStep >= 2 ? "bg-[#E5C558]" : "bg-gray-300"
                  }`}
                ></div>

                <div
                  className={`flex items-center ${
                    processingStep >= 2 ? "text-[#E5C558]" : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold ${
                      processingStep >= 2
                        ? "bg-[#E5C558] border-[#E5C558] text-gray-900"
                        : "border-gray-300"
                    }`}
                  >
                    2
                  </div>
                  <span className="ml-3 font-semibold">Processing</span>
                </div>

                <div
                  className={`h-1 w-16 rounded-full ${
                    processingStep >= 3 ? "bg-[#E5C558]" : "bg-gray-300"
                  }`}
                ></div>

                <div
                  className={`flex items-center ${
                    processingStep >= 3 ? "text-[#E5C558]" : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold ${
                      processingStep >= 3
                        ? "bg-[#E5C558] border-[#E5C558] text-gray-900"
                        : "border-gray-300"
                    }`}
                  >
                    3
                  </div>
                  <span className="ml-3 font-semibold">Download</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Main Content Area */}
          <Reveal>
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Upload Section */}
              <div className="p-8 md:p-12">
                <ImageUpload
                  onImageUpload={handleImageUpload}
                  onUploadStart={handleUploadStart}
                />

                {/* Loading State with Progress */}
                {loading && (
                  <div className="mt-8 text-center">
                    <div className="mb-6">
                      <div className="w-20 h-20 border-4 border-[#E5C558]/20 border-t-[#E5C558] rounded-full animate-spin mx-auto mb-4"></div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Processing Your Image
                      </h3>
                      <p className="text-gray-600">
                        Converting to social media formats...
                      </p>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                      <div
                        className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] h-3 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${conversionProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {conversionProgress}% Complete
                    </p>
                  </div>
                )}

                {/* Error State */}
                {uploadError && (
                  <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-2xl">
                    <div className="flex items-center justify-center mb-4">
                      <svg
                        className="w-12 h-12 text-red-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-red-800 text-center mb-2">
                      Upload Failed
                    </h3>
                    <p className="text-red-600 text-center mb-4">
                      There was an error processing your image. Please try again
                      with a different file.
                    </p>
                    <div className="text-center">
                      <button
                        onClick={handleReset}
                        className="px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Results Section */}
              {!uploadError &&
                Object.values(socialMediaPaths).some(
                  (path) => path !== null
                ) && (
                  <div className="bg-gray-50 border-t">
                    <div className="p-8 md:p-12">
                      <div className="flex items-center justify-between mb-8">
                        <div>
                          <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            Your Converted Images
                          </h2>
                          <p className="text-gray-600">
                            Download individual formats or get them all at once
                          </p>
                        </div>
                        <button
                          onClick={handleReset}
                          className="px-6 py-3 bg-gray-600 text-white font-semibold rounded-xl hover:bg-gray-700 transition-colors flex items-center gap-2"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Convert New Image
                        </button>
                      </div>

                      <SocialMediaFormats socialMediaPaths={socialMediaPaths} />
                    </div>
                  </div>
                )}
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ImageConverter;
